import React from 'react';
import { 
  Container, Typography, Box, Button, Grid, Paper, Stack, 
  Avatar, useTheme, useMediaQuery, alpha 
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import {
  Assignment as ProjectIcon,
  Timeline as DashboardIcon,
  Warning as RiskIcon,
  Description as ReportIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';

const HomePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const features = [
    { 
      title: 'Project Planning', 
      desc: 'Create and track projects with custom timelines, budgets, and priority levels.', 
      icon: <ProjectIcon />, 
      color: theme.palette.primary.main 
    },
    { 
      title: 'Visual Dashboard', 
      desc: 'Monitor project health through interactive charts and real-time data visualization.', 
      icon: <DashboardIcon />, 
      color: theme.palette.secondary.main 
    },
    { 
      title: 'Risk Management', 
      desc: 'Identify, assess, and mitigate risks before they impact your project schedule.', 
      icon: <RiskIcon />, 
      color: '#f59e0b' 
    },
    { 
      title: 'Detailed Reports', 
      desc: 'Generate professional PDF reports and analytics for stakeholders.', 
      icon: <ReportIcon />, 
      color: '#10b981' 
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: { xs: 4, md: 12 }, 
          pb: { xs: 8, md: 16 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="md">
          {/* Logo removed from here as per user request */}
          <Typography 
            variant={isMobile ? "h3" : "h1"} 
            component="h1" 
            gutterBottom 
            sx={{ fontWeight: 800, color: 'text.primary', mb: 3 }}
          >
            Zandrigo <Box component="span" sx={{ color: 'primary.main' }}>Project Manager</Box>
          </Typography>
          <Typography 
            variant="h5" 
            color="text.secondary" 
            paragraph 
            sx={{ mb: 5, maxWidth: '700px', mx: 'auto', lineHeight: 1.6 }}
          >
            Streamlined planning and execution for professional teams. Experience precision tracking and data-driven insights with Zandrigo.
          </Typography>
          
          <Stack 
            direction={isMobile ? "column" : "row"} 
            spacing={2} 
            justifyContent="center"
          >
            {isAuthenticated ? (
              <Button 
                variant="contained" 
                size="large" 
                component={RouterLink} 
                to="/projects"
                endIcon={<ArrowIcon />}
                sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 700 }}
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button 
                  variant="contained" 
                  size="large" 
                  component={RouterLink} 
                  to="/login"
                  sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 700 }}
                >
                  Get Started
                </Button>
                <Button 
                  variant="outlined" 
                  size="large" 
                  component={RouterLink} 
                  to="/contact"
                  sx={{ px: 4, py: 1.5, borderRadius: 2, fontWeight: 700 }}
                >
                  Contact Sales
                </Button>
              </>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 12 }}>
        <Typography 
          variant="h4" 
          textAlign="center" 
          gutterBottom 
          sx={{ fontWeight: 700, mb: 6 }}
        >
          Powerful Features for Efficient Teams
        </Typography>
        <Grid container spacing={4}>
          {features.map((f, index) => (
            <Grid {...{ item: true, xs: 12, sm: 6, md: 3 }} key={index}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  height: '100%', 
                  borderRadius: 4, 
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: alpha(f.color, 0.1), 
                    color: f.color, 
                    mb: 3,
                    width: 56,
                    height: 56
                  }}
                >
                  {f.icon}
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {f.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {f.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Social Proof / Trust Section */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid {...{ item: true, xs: 12, md: 6 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                Loved by project managers worldwide
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
                Join thousands of teams that use Zandrigo to deliver projects on time and within budget. Our intuitive interface reduces training time and increases productivity from day one.
              </Typography>
              <Stack spacing={2}>
                {[
                  'Real-time collaboration across teams',
                  'Advanced risk assessment tools',
                  'Automated reporting and analytics',
                  'Secure data management'
                ].map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SuccessIcon sx={{ color: 'success.main' }} fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{item}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid {...{ item: true, xs: 12, md: 6 }}>
              <Paper 
                elevation={4} 
                sx={{ 
                  p: 2, 
                  borderRadius: 4, 
                  bgcolor: 'background.paper',
                  overflow: 'hidden'
                }}
              >
                <Box 
                  component="img" 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  sx={{ width: '100%', borderRadius: 2 }}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: 12, textAlign: 'center' }}>
        <Paper 
          sx={{ 
            p: { xs: 4, md: 8 }, 
            borderRadius: 6, 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText' 
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to streamline your workflow?
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 4, fontWeight: 400 }}>
            Start managing your projects more effectively today with Zandrigo.
          </Typography>
          <Button 
            variant="contained" 
            color="secondary" 
            size="large"
            component={RouterLink}
            to={isAuthenticated ? "/projects" : "/login"}
            sx={{ px: 6, py: 1.5, borderRadius: 2, fontWeight: 700, color: 'white' }}
          >
            {isAuthenticated ? "Go to Dashboard" : "Get Started Now"}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default HomePage;